import { Injectable, OnDestroy } from '@angular/core';
import { Router, CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, ActivatedRoute, Params } from '@angular/router';
import { ProxyService, GetOrderService, AuthTrackingService, LocationCoreService, UtilitiesService } from '@core/services';
import { environment } from '@environments/environment';
import { ApiRoutes } from '@shared/models/configs/api.endpoints';
import { Constants } from '@core/constants/core.constants';


import { NgRedux, select } from '@angular-redux/store';
import { IAppState, Actions, LocationsEntity, UnitLocation } from '@core/redux/stores';

import { Subscription ,  Observable } from 'rxjs';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class CheckLocationGuard implements CanActivate, OnDestroy {
  @select((appState) => appState.locationSelected) locationSel: Observable<LocationsEntity>;

  private subscriptions = new Subscription();
  private location: LocationsEntity;

  private keyListToCheck = [
    Constants.UNIT_NUMBER,
    Constants.LOCATION_ID,
    Constants.ORDER_ID,
    Constants.FAVORITE_ID,
    Constants.ID,    
  ];

  constructor(
    private activatedRoute: ActivatedRoute,
    private ngRedux: NgRedux<IAppState>,
    private proxy: ProxyService,
    private router: Router,
    private getOrderService: GetOrderService,
    private authService: AuthTrackingService,
    private locationCoreService: LocationCoreService
  ) {
    this.subscriptions.add(
      this.locationSel.subscribe(
        (resp) => {
          this.location = resp;
        }
      )
    );

  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> | boolean {


    // Menu Item deep linking
    const params = { ...route.queryParams };
    if (route.queryParams[Constants.CATEGORY_ID] && route.queryParams[Constants.PARENT_RECIPE_ID]) {
      delete params[Constants.CATEGORY_ID]; delete params[Constants.PARENT_RECIPE_ID];
      if (this.location !== undefined && this.location !== null) {
        this.router.navigate(['/locations/' + this.location.unit + '/menu/categories/' + route.queryParams[Constants.CATEGORY_ID] +
          '/recipes/' + route.queryParams[Constants.PARENT_RECIPE_ID] + '/customization/1'], {queryParams: params});
        return false;
      } else {
        this.router.navigate(['/locations'], { queryParams: UtilitiesService.generateQueryParams([Constants.RETURN_URL], ['/menu/categories/' + route.queryParams[Constants.CATEGORY_ID] +
        '/recipes/' + route.queryParams[Constants.PARENT_RECIPE_ID] + '/customization/1']), ...params});
          return false;
      }
    } else if (route.queryParams[Constants.CATEGORY_ID] && route.queryParams[Constants.PARENT_RECIPE_ID]) {      
      delete params[Constants.CATEGORY_ID]; delete params[Constants.PARENT_RECIPE_ID];
      if (this.location !== undefined && this.location !== null) {        
        this.router.navigate(['/locations/' + this.location.unit + '/menu/categories/' + route.queryParams[Constants.CATEGORY_ID] +
          '/recipes/' + route.queryParams[Constants.PARENT_RECIPE_ID] + '/customization/1'], {queryParams: params });
        return false;
      } else {
        this.router.navigate(['/locations'], { queryParams: UtilitiesService.generateQueryParams([Constants.RETURN_URL], [ '/menu/categories/' + route.queryParams[Constants.CATEGORY_ID] +
        '/recipes/' + route.queryParams[Constants.PARENT_RECIPE_ID] + '/customization/1']) , ...params});
          return false;
      }
    }



    const paramsList = Object.keys(route.queryParams);
    state.url = state.url.replace(/%20/g, '').replace(/['|\.||]/g, '');

    let foundParam = false;
    if (paramsList.length > 0 && paramsList.some(x => this.keyListToCheck.indexOf(x) >= 0)) {
      foundParam = true;
    }

    if (foundParam) {

      // if the location isn't set then see if a parameter for the unit number or location id was passed in
      // with the url. If so, look up the location and set it if one is found. Otherwise redirect the user to the
      // location screen to select one before moving on to the next screen.
      return new Observable<boolean>(
        (observer) => {

          let url = '';
          let notReorder = false;

          if (route.queryParams[Constants.UNIT_NUMBER]) {
            url = ApiRoutes.UNIT_LOCATION + '?q=' + route.queryParams[Constants.UNIT_NUMBER];
            notReorder = true;
          } else if (route.queryParams[Constants.LOCATION_ID]) {
            url = ApiRoutes.LOCATIONS + route.queryParams[Constants.LOCATION_ID];
            notReorder = true;
          }

          if (notReorder && !state.url.match(/\/menu\/presetcustomization/)) {
            this.proxy.httpGet(url).toPromise().then(
              (response) => {
                const data = <UnitLocation>response.body.valueOf();
                  // removed redundant code as both 'if' and 'else' blocks were doing the same thing :)
                  this.ngRedux.dispatch({ type: Actions.SETLOCATIONSELECTED, data: data.locations[0] });
                  this.router.navigate(['/locations/' + data.locations[0].unit + '/menu']);
                  observer.next(true);
                  observer.complete();
              },
              (error) => {
                this.router.navigate(['/locations/menu'], { queryParams:
                   UtilitiesService.generateQueryParams([Constants.RETURN_URL], [state.url])});
                observer.next(false);
                observer.complete();
              }
            );
          } else if (state.url.match(/\/menu\/presetcustomization/) && route.queryParams['id']) {
              const presetCustomizationId = String(route.queryParams['id']);
              if (this.location !== undefined && this.location !== null) {
                  this.getOrderService.getPresetCustomizedOrder(presetCustomizationId, this.authService.isLoggedIn());
                  observer.next(true);
                  observer.complete();
              } else {
                this.locationCoreService.getAssumedLocation().then((success: LocationsEntity) => {
                  this.getOrderService.getPresetCustomizedOrder(presetCustomizationId, this.authService.isLoggedIn());
                  observer.next(true);
                  observer.complete();
                }, (error) => {
                  this.router.navigate(['/locations'],
                   {queryParams: 
                    UtilitiesService.generateQueryParams([Constants.RETURN_URL], 
                      ['/menu/presetcustomization?id=' + presetCustomizationId])});
                  observer.next(false);
                  observer.complete();
                });
              }
          } else {
            const orderId = route.queryParams[Constants.ORDER_ID];
            const automaticDeliveryLocation = route.queryParams[Constants.AUTOMATIC_DELIVERY_LOCATION];            
            
            const orderStatusMsg = route.queryParams[Constants.ORDER_STATUS_MSG];
            if (this.authService.isLoggedIn()) {
              if (this.location !== undefined && this.location !== null) {
                if (orderId) {
                  if (!orderStatusMsg || ( orderStatusMsg &&
                    (orderStatusMsg === Constants.CANCELLED_BY_SYSTEM || orderStatusMsg === Constants.LOCATION_CLOSED ||
                    orderStatusMsg === Constants.CANCELLED_BY_USER_FOR_EDIT)) || orderId.toLowerCase() === Constants.RECENT_ORDER) {
                    this.getOrderService.getOrderAndAppend(orderId, automaticDeliveryLocation);
                    observer.next(true);
                    observer.complete();
                  } else {
                    this.router.navigate(['/locations/' + this.location.unit + '/menu']);
                    observer.next(true);
                    observer.complete();
                  }
                } else if (route.queryParams[Constants.FAVORITE_ID]) {
                  this.getOrderService.getFavoriteOrder(Number(route.queryParams[Constants.FAVORITE_ID]), false);
                  observer.next(true);
                  observer.complete();
                }
              } else {
                if (route.queryParams[Constants.ORDER_ID]) {
                  this.getOrderService.getOrderLocation((route.queryParams[Constants.ORDER_ID]), 'order', false);
                  observer.next(false);
                  observer.complete();
                } else if (route.queryParams[Constants.FAVORITE_ID]) {
                  this.getOrderService.getOrderLocation(Number(route.queryParams[Constants.FAVORITE_ID]), 'favorite');
                  observer.next(false);
                  observer.complete();
                }
              }
            } else {
              const guestId = route.queryParams[Constants.GUEST_ID];
              const guestOrder = route.queryParams[Constants.GUEST_ORDER];
              if (guestOrder) {
                this.getOrderService.getOrderForGuest((route.queryParams[Constants.ORDER_ID]), null, null);
              }
              // If order is opened from deeplink or if order gets cancelled by guest
              else if (!route.queryParams[Constants.USER_TYPE] && (orderId?.toLowerCase() !== Constants.RECENT_ORDER) && !guestId) {
                this.router.navigate(['/home'], { queryParams: UtilitiesService.generateQueryParams([Constants.RETURN_URL, Constants.SIGNIN], [state.url, 'true'])});
              }
              if (this.location !== undefined && this.location !== null) {
                if (orderId && (orderStatusMsg || (orderId && orderId.toLowerCase() === Constants.RECENT_ORDER) || guestId)) {
                  this.getOrderService.getOrderForGuest((route.queryParams[Constants.ORDER_ID]), orderStatusMsg, guestId);
                }
              } else {
                const queryParamsString = new HttpParams({ fromObject: route.queryParams }).toString();
                this.router.navigate(['/locations/menu'], { queryParams: UtilitiesService.generateQueryParams([Constants.RETURN_URL], ['/menu?' + queryParamsString])});
              }
              observer.next(true);
              observer.complete();
            }
          }
        }
      );
    } else if (this.location === undefined || this.location === null) {
      this.locationCoreService.getAssumedLocation().then((success) => {
        if (success && success.unit) {
         return this.stateUrlMatch(state);
        } else {
          this.router.navigate(['/locations'], { queryParams: UtilitiesService.generateQueryParams([Constants.RETURN_URL], [state.url])});
          return false;
        }
      } , () => {
          this.router.navigate(['/locations'], { queryParams: UtilitiesService.generateQueryParams([Constants.RETURN_URL], [state.url]) });
          return false;
        });
    } else {
      return this.stateUrlMatch(state);
    }
  }

  private stateUrlMatch(state: RouterStateSnapshot): boolean {
    if (state.url.match(/^\/locations\/[0-9]\w+\/menu/)) {
      return true;
    } else if (state.url.match(/^\/menu/)) {
      this.router.navigateByUrl(this.addUnitToUrl(state.url, this.location));
      return true;
    } else if (state.url.match(/^\/categories\/[0-9]+\/recipes\/\w+-[0-9]+\/customization\/1/)) {
      this.router.navigate([this.addUnitToUrl(state.url, this.location)]);
      return true;
    }
    return true;
  }

  addUnitToUrl(url: any, location: LocationsEntity) {
    if (url.match(/^\/menu/)) {
      return url.replace(/\/menu/, '/locations/' + location.unit + '/menu');
    } else if (url.match(/^\/categories\/[0-9]+\/recipes\/\w+-[0-9]+\/customization\/1/)) {
      return url.replace(/\/categories\//, '/locations/' + location.unit + '/menu/categories/');
    }
  }
}
